import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { compactInr, money } from "@/lib/format";
import { ArrowUpRight, Warning } from "@phosphor-icons/react";

const KPI = [
  ["Cash & Bank", "cash_and_bank"],
  ["Receivables", "receivables"],
  ["Payables", "payables"],
  ["GST liability", "gst_liability"],
  ["Revenue (FY)", "revenue"],
  ["Net profit", "net_profit"],
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const load = () => api.get("/reports/dashboard").then((r) => setData(r.data)).catch(() => setData(false));

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("fx:refresh", h);
    return () => window.removeEventListener("fx:refresh", h);
  }, []);

  if (!data) return <p className="text-sm text-muted-foreground" data-testid="dashboard-loading">Loading books…</p>;

  const k = data.kpis;
  const health = data.health;

  return (
    <div className="space-y-6" data-testid="dashboard">
      <PageHeader title="Home" subtitle="Live position from posted vouchers — ledgers, GST, banking and AI projections.">
        <Button asChild size="sm" variant="outline" data-testid="dash-new-purchase">
          <Link to="/invoices/purchase/new">New purchase bill</Link>
        </Button>
        <Button asChild size="sm" data-testid="dash-new-sale">
          <Link to="/invoices/sales/new">New sales invoice</Link>
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-px border border-border bg-border lg:grid-cols-6">
        {KPI.map(([label, key]) => (
          <div key={key} className="bg-background p-4" data-testid={`kpi-${key}`}>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
            <p className="num mt-2 text-xl font-semibold tracking-tight">{compactInr(k[key])}</p>
            <p className="num mt-0.5 text-[11px] text-muted-foreground">{money(k[key])}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="panel lg:col-span-2" data-testid="cashflow-forecast">
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <h2 className="font-display text-sm font-bold">90-day AI cashflow projection</h2>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">weekly · ±12% band</span>
          </div>
          <div className="h-[260px] p-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.forecast}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tickFormatter={compactInr} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" width={64} />
                <RTooltip
                  formatter={(v) => money(v)}
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
                />
                <Area type="monotone" dataKey="upper" stroke="none" fill="hsl(var(--chart-1))" fillOpacity={0.08} />
                <Area type="monotone" dataKey="projected" stroke="hsl(var(--chart-1))" strokeWidth={2} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="panel" data-testid="health-score">
          <div className="border-b border-border px-4 py-2.5">
            <h2 className="font-display text-sm font-bold">AI Business Health Score</h2>
          </div>
          <div className="p-4">
            <div className="flex items-end gap-3">
              <p className="num text-5xl font-light leading-none">{health.score}</p>
              <div className="pb-1">
                <p className="text-xs text-muted-foreground">/ 100</p>
                <p className="text-sm font-semibold" data-testid="health-band">{health.band}</p>
              </div>
            </div>
            <div className="mt-3 h-1.5 w-full bg-muted">
              <div
                className="h-1.5 bg-primary"
                style={{ width: `${health.score}%`, transitionProperty: "width", transitionDuration: "600ms" }}
              />
            </div>
            <div className="mt-4 space-y-2">
              {health.factors.map((f) => (
                <div key={f.factor} className="flex items-center justify-between gap-2 text-xs">
                  <span className="truncate text-muted-foreground">{f.factor}</span>
                  <span className="num shrink-0">{f.score} <span className="text-muted-foreground">·{f.weight}%</span></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="panel lg:col-span-2" data-testid="sales-trend">
          <div className="border-b border-border px-4 py-2.5">
            <h2 className="font-display text-sm font-bold">Sales vs purchase (taxable value)</h2>
          </div>
          <div className="h-[220px] p-3">
            {data.trend.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.trend}>
                  <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tickFormatter={compactInr} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" width={64} />
                  <RTooltip formatter={(v) => money(v)}
                    contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="sales" fill="hsl(var(--chart-1))" />
                  <Bar dataKey="purchase" fill="hsl(var(--chart-3))" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="p-6 text-sm text-muted-foreground">No sales or purchase vouchers yet.</p>
            )}
          </div>
        </div>

        <div className="panel" data-testid="compliance-calendar">
          <div className="border-b border-border px-4 py-2.5">
            <h2 className="font-display text-sm font-bold">Compliance calendar</h2>
          </div>
          <table className="dense-table w-full">
            <tbody>
              {data.compliance_calendar.map((c) => (
                <tr key={c.code}>
                  <td>
                    <p className="font-medium">{c.code}</p>
                    <p className="text-[11px] text-muted-foreground">{c.due}</p>
                  </td>
                  <td className="text-right">
                    <span className="bg-destructive/10 px-1.5 py-0.5 text-[10px] uppercase tracking-widest text-destructive">
                      {c.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {k.unmatched_bank_lines > 0 && (
            <Link
              to="/banking"
              className="flex items-center gap-2 border-t border-border px-4 py-3 text-xs text-muted-foreground hover:text-foreground"
              data-testid="unmatched-bank-alert"
            >
              <Warning size={14} className="text-destructive" />
              {k.unmatched_bank_lines} unmatched bank lines
              <ArrowUpRight size={12} className="ml-auto" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
